import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import styled, { css } from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import Table from '../../../../common/src/components/Table'
import { IntroText, H4, Spacer, BodyText, BodyTextSpan, BodyMonoText } from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { dateMDYFmt } from '../../../../common/src/date'
import { addFeedback } from '../../../../common/src/feedback'
import { formatUSD, formatInt } from '../../../../common/src/format'
import { FetchAvailabilityQuery, FetchAvailabilityQueryVariables, TaskType } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { PollingQuery } from '../components/PollingQuery'
import { getFirmPath } from '../route'

const taskAvailFrag = gql`
  fragment TaskAvail on LeasingTask {
    id
    taskType
    isArchived
    firm {
      id
      firmName
    }
    address {
      id
      today
    }
    floorPlans {
      bedrooms
      bathrooms
      sqft
      name
      description
      minRentUSD
      maxRentUSD
      num
      numShowable
      availableOn
      showableOn
    }
    pmItemKeys {
      externalPmUrl
      pmType
      firm
      property
    }
  }
`

const availabilityQuery = gql`
  query FetchAvailability($taskId: Int!) {
    task(taskId: $taskId) {
      ...TaskAvail
    }
  }
  ${taskAvailFrag}
`

const unlinkTaskMutation = gql`
  mutation UnlinkTask($taskId: Int!) {
    unlinkTask(taskId: $taskId) {
      ...TaskAvail
    }
  }
  ${taskAvailFrag}
`

type FloorPlan = FetchAvailabilityQuery['task']['floorPlans'][0]

const DASH = '—'

const TR = styled.tr`
  ${({ row }: { row: FloorPlan }) => css`
    color: ${row.numShowable > 0 ? Colors.snow : Colors.silver};
    &:hover {
      background: ${Colors.black};
    }
  `};
`

const TableHeaderCell = styled.th`
  padding: 2px 8px 2px 0;
  &:last-child {
    padding: 2px 0;
  }
`

function TableRow({ row, children }: { row: FloorPlan; children: React.ReactNode }) {
  const parts = []
  if (row.name) {
    parts.push(row.name)
  }
  if (row.description) {
    parts.push(row.description)
  }
  if (row.minRentUSD && row.maxRentUSD && row.maxRentUSD > row.minRentUSD) {
    parts.push(`Rent ${formatUSD(row.minRentUSD)} - ${formatUSD(row.maxRentUSD)}`)
  }
  const title = parts.join(', ')

  return (
    <TR row={row} title={title}>
      {children}
    </TR>
  )
}

interface AvailabilitySectionProps {
  /** Show floor plans and PM links for the given task. */
  taskId: number
  /** Let the user unlink the task from PM properties. Only admins have this capability. */
  showUnlink?: boolean
  /** Show a link to PM sync history for the task / firm. Only admins have access to this. */
  showHistoryLink?: boolean
}

class AvailabilitySectionImpl extends React.PureComponent<
  AvailabilitySectionProps & QueryResult<FetchAvailabilityQuery, FetchAvailabilityQueryVariables>
> {
  render() {
    const { data, showUnlink, showHistoryLink } = this.props
    if (data == null || data.task == null) {
      return null
    }
    const { floorPlans, pmItemKeys, address, firm, taskType } = data.task
    if (floorPlans == null) {
      return null
    }
    if (taskType !== TaskType.LEASING_PROPERTY) {
      return <IntroText>Not a leasing property task.</IntroText>
    }
    const hasPm = pmItemKeys.length > 0

    const headers = ['Name', 'Bed / ba', 'Sqft', '# units', 'Rent', 'Available', 'Showable']
    const widths = [null, '60px', '54px', '54px', '64px', '54px', '54px']
    const aligns = ['left', 'right', 'right', 'right', 'right', 'right', 'right']

    return (
      <div>
        <H4>Floor plans</H4>
        <Spacer height={16} />
        {!hasPm && <BodyText color="lemon">No PM URLs, so the task is archived. Bot off.</BodyText>}
        {address == null && <BodyText color="lemon">Invalid address.</BodyText>}
        {hasPm && floorPlans.length === 0 && (
          <div>
            <BodyText color="lemon">No floor plans found, bot off.</BodyText>
            <Spacer height={8} />
            <BodyText>
              Property may be hidden, deleted, might have zero units, or we might no longer have access to it.
            </BodyText>
            <Spacer height={16} />
          </div>
        )}
        {hasPm && address != null && floorPlans.length > 0 && (
          <div>
            <Table
              rows={floorPlans}
              columns={this.getCols(address.today)}
              headers={headers}
              widths={widths}
              alignments={aligns}
              dataRowComponent={TableRow}
              headerCellComponent={TableHeaderCell}
            />
            <Spacer height={8} />
            <BodyText>
              Highlighted rows are showable: Lisa will schedule showings. Other rows are not showable: Lisa will not
              create new showings for those unit types, but will keep existing pending showings.
            </BodyText>
            <Spacer height={16} />
          </div>
        )}
        {hasPm && <IntroText>Syncing pricing and availability from:</IntroText>}
        {hasPm && <Spacer height={8} />}
        {hasPm &&
          pmItemKeys.map((p: FetchAvailabilityQuery['task']['pmItemKeys'][0], i: number) => (
            <BodyText key={i}>
              {PmItemLink(p)}
              <Spacer height={8} />
            </BodyText>
          ))}
        {hasPm && showUnlink && (
          <>
            <Button size="small" onClick={this.unlink}>
              Unlink
            </Button>
            <Spacer width={8} />
            <BodyTextSpan>Only if the task was created by mistake.</BodyTextSpan>
            <Spacer height={8} />
          </>
        )}
        {hasPm && showHistoryLink && (
          <>
            <BodyText>
              For sync history, see <Link to={getFirmPath(firm.id)}>{firm.firmName}</Link>.
            </BodyText>
          </>
        )}
        <Spacer height={32} />
      </div>
    )
  }

  private getCols(today: string) {
    return [
      (v: FloorPlan) => nullDash(v.description || v.name),
      (v: FloorPlan) =>
        (v.bedrooms == null ? DASH : v.bedrooms.toFixed(1)) +
        ' / ' +
        (v.bathrooms == null ? DASH : v.bathrooms.toFixed(1)),
      (v: FloorPlan) => nullZeroDash(v.sqft, formatInt),
      (v: FloorPlan) => formatInt(v.num),
      (v: FloorPlan) => nullZeroDash(v.minRentUSD, formatUSD) + ((v.maxRentUSD || 0) > (v.minRentUSD || 0) ? '+' : ''),
      (v: FloorPlan) => {
        if (v.availableOn == null || v.availableOn <= today) {
          return v.numShowable > 0 ? 'Yes' : DASH
        }
        return dateMDYFmt(v.availableOn, 'UTC')
      },
      (v: FloorPlan) => {
        if (v.showableOn == null || v.showableOn <= today) {
          return v.numShowable > 0 ? 'Yes' : DASH
        }
        return v.showableOn ? dateMDYFmt(v.showableOn, 'UTC') : ''
      },
    ] as ((v: FloorPlan) => string)[]
  }

  private unlink = () => {
    if (
      !window.confirm(
        'Are you sure? This permanently archives the task. ' +
          'Use it for accidentally created tasks. ' +
          'Do not use it for off-boarding or stopping showings.' +
          "If there's another reason, please let engineering know."
      )
    ) {
      return
    }
    apolloWebClient
      .mutate({
        mutation: unlinkTaskMutation,
        variables: {
          taskId: this.props.taskId,
        },
      })
      .then(() => addFeedback('Unlinked task'))
      .catch(handleError)
  }
}

function PmItemLink(p: FetchAvailabilityQuery['task']['pmItemKeys'][0]): React.ReactNode {
  return (
    <span>
      {!p.externalPmUrl && (
        <BodyMonoText as="span">
          » {p.pmType}/firm/{p.firm}/property/{p.property}
        </BodyMonoText>
      )}
      {p.externalPmUrl && <Link href={p.externalPmUrl}>» {p.externalPmUrl}</Link>}
      <Spacer width={8} />
      &middot;
      <Spacer width={8} />
      <Link href={`/leasing/data/pmitem?pmType=${p.pmType}&firm=${p.firm}&property=${p.property}`}>data</Link>
    </span>
  )
}

function nullDash(val: any, fn?: (val: any) => string): string {
  return val == null ? DASH : fn ? fn(val) : '' + val
}

function nullZeroDash(val?: number | null, fn?: (val: number) => string): string {
  return val == null || val === 0 ? DASH : fn ? fn(val) : '' + val
}

export default function AvailabilitySection(props: AvailabilitySectionProps) {
  return (
    <PollingQuery query={availabilityQuery} variables={{ taskId: props.taskId }}>
      {(res: QueryResult<FetchAvailabilityQuery, FetchAvailabilityQueryVariables>) => (
        <AvailabilitySectionImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}
